// =========================================================
// DEBUTS CROP
// PREMIUM GROW SHOP
//
// ProductDetailPage.jsx
//
// PRODUCT DETAIL · RED / NEON / PREMIUM EDITION
//
// FLUJO:
//
// PRODUCTO
//    ↓
// AGREGAR AL CARRITO
//    ↓
// /cart
//    ↓
// DOMICILIO / RECOGER EN TIENDA
//    ↓
// /checkout
//    ↓
// PAGO
//    ↓
// COMPROBANTE
//    ↓
// VERIFICACIÓN
//    ↓
// PIN
//
// =========================================================

import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Heart,
  MessageCircle,
  Minus,
  Package,
  Plus,
  ShoppingCart,
  ShieldCheck,
  Star,
  Tag,
  Truck,
  Zap,
} from "lucide-react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import API from "../../services/api";

import Navbar from "../../components/common/Navbar";
import Footer from "../../components/common/Footer";

import "../../styles/product-detail.css";


// =========================================================
// CONFIG
// =========================================================

const CART_KEY = "cart";

const PLACEHOLDER_IMAGE =
  "/placeholder.png";


// =========================================================
// FAVORITES API
// =========================================================

const FAVORITES_ENDPOINT =
  "/reviews/favorites/";


// =========================================================
// AUTH TOKEN
// =========================================================

function getAuthToken() {
  try {
    const raw =
      localStorage.getItem(
        "auth"
      );

    if (!raw) {
      return "";
    }

    const auth =
      JSON.parse(raw);

    return (
      auth?.access ||
      ""
    );
  } catch (error) {
    console.error(
      "DEBUTS CROP · Error leyendo autenticación:",
      error
    );

    return "";
  }
}


// =========================================================
// API AUTH CONFIG
// =========================================================

function getAuthConfig() {
  const token =
    getAuthToken();

  if (!token) {
    return {};
  }

  return {
    headers: {
      Authorization:
        `Bearer ${token}`,
    },
  };
}


// =========================================================
// HELPERS
// =========================================================

function getImage(value) {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  return (
    value?.image ||
    value?.image_url ||
    value?.url ||
    value?.src ||
    ""
  );
}


// =========================================================
// PRODUCT IMAGES
// =========================================================

function getProductImages(product) {
  const images = [];

  const candidates = [
    ...(Array.isArray(product?.images)
      ? product.images
      : []),

    ...(Array.isArray(product?.gallery)
      ? product.gallery
      : []),

    ...(Array.isArray(product?.photos)
      ? product.photos
      : []),

    ...(Array.isArray(product?.product_images)
      ? product.product_images
      : []),
  ];

  candidates.forEach((item) => {
    const image =
      getImage(item);

    if (
      image &&
      !images.includes(image)
    ) {
      images.push(image);
    }
  });

  const mainImage =
    product?.image ||
    product?.image_url ||
    product?.thumbnail ||
    "";

  if (
    mainImage &&
    !images.includes(mainImage)
  ) {
    images.unshift(
      mainImage
    );
  }

  return images;
}


// =========================================================
// PRICE
// =========================================================

function formatPrice(value) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "";
  }

  if (
    typeof value === "string" &&
    value.includes("$")
  ) {
    return value;
  }

  const number =
    Number(value);

  if (Number.isNaN(number)) {
    return String(value);
  }

  return new Intl.NumberFormat(
    "es-CO",
    {
      style: "currency",
      currency: "COP",
      maximumFractionDigits: 0,
    }
  ).format(number);
}


// =========================================================
// ORIGINAL PRICE
// =========================================================

function getOriginalPrice(product) {
  if (!product) {
    return null;
  }

  const candidates = [
    product.original_price,
    product.compare_price,
    product.old_price,
    product.regular_price,
  ];

  for (const value of candidates) {
    if (
      value !== null &&
      value !== undefined &&
      value !== ""
    ) {
      const number =
        Number(value);

      if (
        !Number.isNaN(number) &&
        number > 0
      ) {
        return number;
      }
    }
  }

  const discount =
    Number(
      product.discount_percentage ||
        0
    );

  const current =
    Number(
      product.price ||
        0
    );

  if (
    discount > 0 &&
    current > 0 &&
    discount < 100
  ) {
    return Math.round(
      current /
        (1 - discount / 100)
    );
  }

  return null;
}


// =========================================================
// DISCOUNT
// =========================================================

function getDiscount(product) {
  const discount =
    Number(
      product?.discount_percentage ||
        0
    );

  return discount > 0
    ? discount
    : 0;
}


// =========================================================
// NORMALIZE LIST
// =========================================================

function normalizeList(value) {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    return value
      .split(/\r?\n|,|;/)
      .map(
        (item) =>
          item.trim()
      )
      .filter(Boolean);
  }

  return [];
}


// =========================================================
// PRODUCT ID
// =========================================================

function getProductId(product) {
  return (
    product?.id ||
    product?.pk ||
    product?.product_id
  );
}


// =========================================================
// CART
// =========================================================

function readCart() {
  try {
    const raw =
      localStorage.getItem(
        CART_KEY
      );

    if (!raw) {
      return [];
    }

    const parsed =
      JSON.parse(raw);

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed;
  } catch (error) {
    console.error(
      "DEBUTS CROP · Error leyendo carrito:",
      error
    );

    return [];
  }
}


// =========================================================
// SAVE CART
// =========================================================

function saveCart(items) {
  const safeItems =
    Array.isArray(items)
      ? items
      : [];

  localStorage.setItem(
    CART_KEY,
    JSON.stringify(
      safeItems
    )
  );

  window.dispatchEvent(
    new Event(
      "cartUpdated"
    )
  );

  window.dispatchEvent(
    new StorageEvent(
      "storage",
      {
        key: CART_KEY,
        newValue:
          JSON.stringify(
            safeItems
          ),
        oldValue: null,
        storageArea:
          localStorage,
      }
    )
  );
}


// =========================================================
// NORMALIZE CART PRODUCT
// =========================================================

function normalizeCartProduct(
  product,
  quantity
) {
  const productId =
    getProductId(
      product
    );

  const numericPrice =
    Number(
      product?.price ??
        product?.sale_price ??
        0
    );

  const image =
    product?.image ||
    product?.image_url ||
    product?.thumbnail ||
    "";

  const category =
    product?.category_name ||
    product?.category ||
    "Premium Grow Shop";

  return {
    id: productId,

    product_id:
      productId,

    name:
      product?.name ||
      "Producto Debuts Crop",

    price:
      numericPrice,

    sale_price:
      Number(
        product?.sale_price ??
          product?.price ??
          0
      ),

    quantity:
      Number(quantity) || 1,

    qty:
      Number(quantity) || 1,

    stock:
      Number(
        product?.stock ?? 0
      ),

    image,

    image_url:
      product?.image_url ||
      image,

    thumbnail:
      product?.thumbnail ||
      image,

    category,

    category_name:
      category,

    sku:
      product?.sku ||
      product?.code ||
      product?.reference ||
      "",

    brand:
      product?.brand_name ||
      product?.brand ||
      "",

    added_from:
      "product-detail",

    added_at:
      new Date().toISOString(),
  };
}


// =========================================================
// COMPONENT
// =========================================================

export default function ProductDetailPage() {
  const { id } =
    useParams();

  const navigate =
    useNavigate();


  // =======================================================
  // STATE
  // =======================================================

  const [
    product,
    setProduct,
  ] = useState(null);

  const [
    images,
    setImages,
  ] = useState([]);

  const [
    activeImage,
    setActiveImage,
  ] = useState("");

  const [
    quantity,
    setQuantity,
  ] = useState(1);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");

  const [
    activeTab,
    setActiveTab,
  ] = useState(
    "description"
  );

  const [
    openFeature,
    setOpenFeature,
  ] = useState(null);

  const [
    favorite,
    setFavorite,
  ] = useState(false);

  const [
    favoriteLoading,
    setFavoriteLoading,
  ] = useState(false);

  const [
    relatedProducts,
    setRelatedProducts,
  ] = useState([]);

  const [
    cartMessage,
    setCartMessage,
  ] = useState("");

  const [
    buyingNow,
    setBuyingNow,
  ] = useState(false);

  const [
    addingToCart,
    setAddingToCart,
  ] = useState(false);

  const selectionRegistered =
    useRef(null);


  // =======================================================
  // LOAD PRODUCT
  // =======================================================

  useEffect(() => {
    let mounted = true;

    const loadProduct =
      async () => {
        try {
          setLoading(true);
          setError("");

          const response =
            await API.get(
              `/products/${id}/`
            );

          const data =
            response.data;

          if (
            !data ||
            !getProductId(data)
          ) {
            if (mounted) {
              setError(
                "Producto no encontrado."
              );
            }

            return;
          }

          if (!mounted) {
            return;
          }

          setProduct(data);

          // -------------------------------------------------
          // IMÁGENES
          // -------------------------------------------------

          const productImages =
            getProductImages(
              data
            );

          const safeImages =
            productImages.length > 0
              ? productImages
              : [
                  PLACEHOLDER_IMAGE,
                ];

          setImages(
            safeImages
          );

          setActiveImage(
            safeImages[0]
          );

          // -------------------------------------------------
          // RELATED
          // -------------------------------------------------

          const related =
            data?.related_products ||
            data?.related ||
            data?.recommendations ||
            [];

          if (
            Array.isArray(
              related
            )
          ) {
            setRelatedProducts(
              related
            );
          }

          // -------------------------------------------------
          // REGISTER SELECTION
          // -------------------------------------------------

          if (
            selectionRegistered.current !==
            id
          ) {
            selectionRegistered.current =
              id;

            try {
              const selectionResponse =
                await API.post(
                  `/products/${id}/select/`
                );

              const newSelectionCount =
                selectionResponse
                  ?.data
                  ?.data
                  ?.selection_count;

              if (
                newSelectionCount !==
                  undefined &&
                mounted
              ) {
                setProduct(
                  (current) => {
                    if (!current) {
                      return current;
                    }

                    return {
                      ...current,

                      selection_count:
                        newSelectionCount,
                    };
                  }
                );
              }
            } catch (
              selectionError
            ) {
              console.error(
                "DEBUTS CROP · Error registrando selección:",
                selectionError
              );

              selectionRegistered.current =
                null;
            }
          }
        } catch (err) {
          console.error(
            "DEBUTS CROP · Error cargando producto:",
            err
          );

          if (mounted) {
            setError(
              err?.response
                ?.data?.detail ||
                err?.response
                  ?.data?.message ||
                "Error cargando producto."
            );
          }
        } finally {
          if (mounted) {
            setLoading(
              false
            );
          }
        }
      };

    loadProduct();

    return () => {
      mounted = false;
    };
  }, [id]);


  // =======================================================
  // LOAD FAVORITE STATUS
  // =======================================================

  useEffect(() => {
    let mounted = true;

    const loadFavorite =
      async () => {
        const token =
          getAuthToken();

        // -------------------------------------------------
        // SIN SESIÓN
        // -------------------------------------------------

        if (!token) {
          if (mounted) {
            setFavorite(
              false
            );
          }

          return;
        }

        try {
          const response =
            await API.get(
              FAVORITES_ENDPOINT,
              getAuthConfig()
            );

          const data =
            response?.data;

          // -------------------------------------------------
          // API puede devolver:
          //
          // []
          //
          // o:
          // {
          //   results: []
          // }
          //
          // o:
          // {
          //   data: []
          // }
          // -------------------------------------------------

          const favorites =
            Array.isArray(data)
              ? data
              : Array.isArray(
                  data?.results
                )
              ? data.results
              : Array.isArray(
                  data?.data
                )
              ? data.data
              : [];

          const currentProductId =
            String(id);

          const exists =
            favorites.some(
              (item) => {
                const favoriteProductId =
                  item?.product_id ??
                  item?.product?.id ??
                  item?.product?.pk ??
                  item?.product;

                return (
                  favoriteProductId !==
                    null &&
                  favoriteProductId !==
                    undefined &&
                  String(
                    favoriteProductId
                  ) ===
                    currentProductId
                );
              }
            );

          if (mounted) {
            setFavorite(
              exists
            );
          }
        } catch (favoriteError) {
          console.error(
            "DEBUTS CROP · Error consultando favorito:",
            favoriteError
          );

          if (mounted) {
            setFavorite(
              false
            );
          }
        }
      };

    loadFavorite();

    return () => {
      mounted = false;
    };
  }, [id]);


  // =======================================================
  // TOGGLE FAVORITE
  // =======================================================

  const handleToggleFavorite =
    async () => {
      // ---------------------------------------------------
      // EVITAR DOBLE CLICK
      // ---------------------------------------------------

      if (favoriteLoading) {
        return;
      }

      // ---------------------------------------------------
      // PRODUCT ID
      // ---------------------------------------------------

      const productId =
        getProductId(
          product
        );

      if (!productId) {
        console.error(
          "DEBUTS CROP · No se encontró el ID del producto para favoritos."
        );

        return;
      }

      // ---------------------------------------------------
      // AUTH
      // ---------------------------------------------------

      const token =
        getAuthToken();

      if (!token) {
        window.alert(
          "Debes iniciar sesión para guardar productos en favoritos."
        );

        return;
      }

      // ---------------------------------------------------
      // LOCK
      // ---------------------------------------------------

      setFavoriteLoading(
        true
      );

      // ---------------------------------------------------
      // GUARDAR FAVORITO
      // ---------------------------------------------------

      if (!favorite) {
        try {
          await API.post(
            FAVORITES_ENDPOINT,
            {
              product_id:
                productId,
            },
            getAuthConfig()
          );

          setFavorite(
            true
          );

          console.log(
            "DEBUTS CROP · Producto agregado a favoritos:",
            productId
          );
        } catch (
          favoriteError
        ) {
          console.error(
            "DEBUTS CROP · Error agregando favorito:",
            favoriteError
          );

          const status =
            favoriteError?.response
              ?.status;

          // -------------------------------------------------
          // YA EXISTÍA
          //
          // Si el backend responde 400
          // porque ya existe, dejamos
          // el estado sincronizado.
          // -------------------------------------------------

          if (
            status === 400 ||
            status === 409
          ) {
            setFavorite(
              true
            );
          } else if (
            status === 401 ||
            status === 403
          ) {
            window.alert(
              "Tu sesión ya no es válida. Inicia sesión nuevamente."
            );
          } else {
            window.alert(
              favoriteError
                ?.response
                ?.data
                ?.detail ||
                favoriteError
                  ?.response
                  ?.data
                  ?.message ||
                "No fue posible guardar el producto en favoritos."
            );
          }
        } finally {
          setFavoriteLoading(
            false
          );
        }

        return;
      }

      // ---------------------------------------------------
      // ELIMINAR FAVORITO
      // ---------------------------------------------------

      try {
        await API.delete(
          `${FAVORITES_ENDPOINT}product/${productId}/`,
          getAuthConfig()
        );

        setFavorite(
          false
        );

        console.log(
          "DEBUTS CROP · Producto eliminado de favoritos:",
          productId
        );
      } catch (
        favoriteError
      ) {
        console.error(
          "DEBUTS CROP · Error eliminando favorito:",
          favoriteError
        );

        const status =
          favoriteError?.response
            ?.status;

        if (
          status === 401 ||
          status === 403
        ) {
          window.alert(
            "Tu sesión ya no es válida. Inicia sesión nuevamente."
          );
        } else if (
          status === 404
        ) {
          // -----------------------------------------------
          // Si ya no existe en backend,
          // sincronizamos el frontend.
          // -----------------------------------------------

          setFavorite(
            false
          );
        } else {
          window.alert(
            favoriteError
              ?.response
              ?.data
              ?.detail ||
              favoriteError
                ?.response
                ?.data
                ?.message ||
              "No fue posible quitar el producto de favoritos."
          );
        }
      } finally {
        setFavoriteLoading(
          false
        );
      }
    };


  // =======================================================
  // DERIVED DATA
  // =======================================================

  const discount =
    useMemo(
      () =>
        getDiscount(
          product
        ),
      [product]
    );

  const originalPrice =
    useMemo(
      () =>
        getOriginalPrice(
          product
        ),
      [product]
    );

  const currentPrice =
    useMemo(() => {
      if (!product) {
        return "";
      }

      return (
        product.formatted_price ||
        formatPrice(
          product.sale_price ??
            product.price
        )
      );
    }, [product]);


  const numericStock =
    Number(
      product?.stock ?? 0
    );

  const outOfStock =
    numericStock <= 0;

  const maxQuantity =
    Math.max(
      numericStock || 99,
      1
    );


  // =======================================================
  // PRODUCT DATA
  // =======================================================

  const category =
    product?.category_name ||
    product?.category ||
    "Premium Grow Shop";

  const description =
    product?.description ||
    "Producto seleccionado por Debuts Crop para ofrecer una experiencia premium.";

  const rating =
    Number(
      product?.rating ||
        product?.average_rating ||
        5
    );

  const reviewCount =
    product?.review_count ||
    product?.reviews_count ||
    0;

  const brand =
    product?.brand_name ||
    product?.brand ||
    "";

  const sku =
    product?.sku ||
    product?.code ||
    product?.reference ||
    "";


  // =======================================================
  // FEATURES
  // =======================================================

  const productFeatures =
    normalizeList(
      product?.features
    );

  const features =
    productFeatures.length > 0
      ? productFeatures
      : [
          "Producto original y seleccionado",
          "Diseño compacto y portátil",
          "Experiencia premium",
          "Soporte personalizado Debuts Crop",
          "Envíos a todo Colombia",
        ];


  // =======================================================
  // SPECIFICATIONS
  // =======================================================

  const specifications =
    normalizeList(
      product?.specifications
    ).length > 0
      ? normalizeList(
          product?.specifications
        )
      : normalizeList(
          product?.specs
        );

  const safeSpecifications =
    specifications.length > 0
      ? specifications
      : [
          brand
            ? `Marca: ${brand}`
            : "Marca: Debuts Crop",

          sku
            ? `Referencia: ${sku}`
            : "Referencia disponible al consultar",

          `Categoría: ${category}`,
        ];


  // =======================================================
  // BOX CONTENT
  // =======================================================

  const boxContents =
    normalizeList(
      product?.box_contents
    ).length > 0
      ? normalizeList(
          product?.box_contents
        )
      : normalizeList(
          product?.contents
        );

  const safeBoxContents =
    boxContents.length > 0
      ? boxContents
      : [
          "Producto principal",
          "Accesorios originales según referencia",
          "Manual o información del producto",
        ];


  // =======================================================
  // WHATSAPP
  // =======================================================

  const whatsappText = `
🌿 Hola Debuts Crop 👋

Estoy interesado en este producto.

━━━━━━━━━━━━━━━━━━━━━━

🌱 Producto:
${product?.name || ""}

📦 Categoría:
${category}

💲 Precio:
${currentPrice}

🔢 Cantidad:
${quantity}

🖼 Imagen:
${activeImage}

🔗 Producto:
${
  typeof window !==
  "undefined"
    ? `${window.location.origin}/products/${getProductId(
        product
      )}`
    : ""
}

━━━━━━━━━━━━━━━━━━━━━━

¿Está disponible?

Gracias. 🙌
`.trim();

  const whatsappLink =
    `https://wa.me/573022242892?text=${encodeURIComponent(
      whatsappText
    )}`;


  // =======================================================
  // ADD TO CART
  // =======================================================

  const addProductToCart =
    () => {
      if (!product) {
        return false;
      }

      // ---------------------------------------------------
      // STOCK
      // ---------------------------------------------------

      if (outOfStock) {
        window.alert(
          "Este producto actualmente no tiene stock disponible."
        );

        return false;
      }

      // ---------------------------------------------------
      // PRODUCT ID
      // ---------------------------------------------------

      const productId =
        getProductId(
          product
        );

      if (!productId) {
        window.alert(
          "No fue posible identificar el producto."
        );

        return false;
      }

      setAddingToCart(
        true
      );

      try {
        // -------------------------------------------------
        // LEER CARRITO ACTUAL
        // -------------------------------------------------

        const currentCart =
          readCart();

        // -------------------------------------------------
        // BUSCAR PRODUCTO EXISTENTE
        // -------------------------------------------------

        const existingIndex =
          currentCart.findIndex(
            (item) =>
              String(
                item?.product_id ??
                  item?.id
              ) ===
              String(
                productId
              )
          );

        // -------------------------------------------------
        // PRODUCTO YA EXISTE
        // -------------------------------------------------

        if (
          existingIndex >= 0
        ) {
          const existing =
            currentCart[
              existingIndex
            ];

          const currentQuantity =
            Number(
              existing?.quantity ??
                existing?.qty ??
                1
            );

          const stockLimit =
            Number(
              product?.stock
            ) || 999;

          const newQuantity =
            Math.min(
              currentQuantity +
                quantity,
              stockLimit
            );

          currentCart[
            existingIndex
          ] = {
            ...existing,

            ...normalizeCartProduct(
              product,
              newQuantity
            ),
          };
        }

        // -------------------------------------------------
        // PRODUCTO NUEVO
        // -------------------------------------------------

        else {
          currentCart.push(
            normalizeCartProduct(
              product,
              quantity
            )
          );
        }

        // -------------------------------------------------
        // GUARDAR
        // -------------------------------------------------

        saveCart(
          currentCart
        );

        // -------------------------------------------------
        // VERIFICACIÓN REAL
        // -------------------------------------------------

        const savedCart =
          readCart();

        const savedProduct =
          savedCart.find(
            (item) =>
              String(
                item?.product_id ??
                  item?.id
              ) ===
              String(
                productId
              )
          );

        if (!savedProduct) {
          console.error(
            "DEBUTS CROP · El carrito no pudo verificarse después de guardar."
          );

          window.alert(
            "No fue posible guardar el producto en el carrito. Intenta nuevamente."
          );

          return false;
        }

        // -------------------------------------------------
        // MENSAJE
        // -------------------------------------------------

        setCartMessage(
          quantity > 1
            ? `${quantity} unidades agregadas al carrito.`
            : "Producto agregado al carrito."
        );

        window.setTimeout(
          () => {
            setCartMessage(
              ""
            );
          },
          3000
        );

        return true;
      } catch (
        cartError
      ) {
        console.error(
          "DEBUTS CROP · Error agregando al carrito:",
          cartError
        );

        window.alert(
          "No fue posible agregar el producto al carrito."
        );

        return false;
      } finally {
        setAddingToCart(
          false
        );
      }
    };


  // =======================================================
  // BUY NOW
  // =======================================================

  const handleBuyNow =
    async () => {
      if (buyingNow) {
        return;
      }

      if (!product) {
        return;
      }

      if (outOfStock) {
        window.alert(
          "Este producto actualmente no tiene stock disponible."
        );

        return;
      }

      setBuyingNow(
        true
      );

      try {
        const added =
          addProductToCart();

        if (!added) {
          return;
        }

        const cart =
          readCart();

        if (
          !Array.isArray(
            cart
          ) ||
          cart.length === 0
        ) {
          console.error(
            "DEBUTS CROP · Checkout bloqueado: carrito vacío."
          );

          window.alert(
            "El producto no pudo quedar guardado en el carrito."
          );

          return;
        }

        const productId =
          getProductId(
            product
          );

        const cartProduct =
          cart.find(
            (item) =>
              String(
                item?.product_id ??
                  item?.id
              ) ===
              String(
                productId
              )
          );

        if (!cartProduct) {
          console.error(
            "DEBUTS CROP · El producto no aparece en el carrito:",
            productId,
            cart
          );

          window.alert(
            "No fue posible preparar el producto para el checkout."
          );

          return;
        }

        console.log(
          "DEBUTS CROP · Carrito listo para checkout:",
          cart
        );

        navigate(
          "/checkout"
        );
      } catch (error) {
        console.error(
          "DEBUTS CROP · Error preparando checkout:",
          error
        );

        window.alert(
          "No fue posible iniciar el checkout."
        );
      } finally {
        window.setTimeout(
          () => {
            setBuyingNow(
              false
            );
          },
          500
        );
      }
    };


  // =======================================================
  // GO CART
  // =======================================================

  const handleGoToCart =
    () => {
      navigate(
        "/cart"
      );
    };


  // =======================================================
  // QUANTITY
  // =======================================================

  const increaseQuantity =
    () => {
      setQuantity(
        (current) =>
          Math.min(
            current + 1,
            maxQuantity
          )
      );
    };


  const decreaseQuantity =
    () => {
      setQuantity(
        (current) =>
          Math.max(
            current - 1,
            1
          )
      );
    };


  const handleQuantityInput =
    (event) => {
      const value =
        Number(
          event.target.value
        );

      if (
        Number.isNaN(
          value
        )
      ) {
        return;
      }

      setQuantity(
        Math.min(
          Math.max(
            Math.floor(
              value
            ),
            1
          ),
          maxQuantity
        )
      );
    };


  // =======================================================
  // RELATED PRODUCT
  // =======================================================

  const handleRelatedProduct =
    (related) => {
      const relatedId =
        getProductId(
          related
        );

      if (relatedId) {
        navigate(
          `/products/${relatedId}`
        );
      }
    };


  // =======================================================
  // LOADING
  // =======================================================

  if (loading) {
    return (
      <>
        <Navbar />

        <main className="product-detail-page product-detail-loading">

          <div className="product-detail-loader">

            <div className="loader-ring" />

            <span>
              CARGANDO PRODUCTO
            </span>

            <small>
              DEBUTS CROP · PREMIUM GROW SHOP
            </small>

          </div>

        </main>

        <Footer />
      </>
    );
  }


  // =======================================================
  // ERROR
  // =======================================================

  if (error) {
    return (
      <>
        <Navbar />

        <main className="product-detail-page product-detail-error">

          <div className="product-error-panel">

            <span className="technical-label">
              PRODUCT SYSTEM
            </span>

            <h2>
              {error}
            </h2>

            <button
              type="button"
              className="product-back-button"
              onClick={() =>
                navigate(-1)
              }
            >
              <ArrowLeft
                size={18}
              />

              VOLVER
            </button>

          </div>

        </main>

        <Footer />
      </>
    );
  }


  // =======================================================
  // NO PRODUCT
  // =======================================================

  if (!product) {
    return null;
  }


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <>
      <Navbar />

      <main className="product-detail-page">

        {/* =================================================
            BREADCRUMB
        ================================================= */}

        <div className="product-breadcrumb">

          <button
            type="button"
            onClick={() =>
              navigate(-1)
            }
            className="breadcrumb-back"
          >
            <ArrowLeft
              size={15}
            />

            Inicio
          </button>

          <span>/</span>

          <span>
            {category}
          </span>

          <span>/</span>

          <strong>
            {product.name}
          </strong>

        </div>


        {/* =================================================
            PRODUCT HERO
        ================================================= */}

        <section className="product-detail-hero">

          {/* =================================================
              GALLERY
          ================================================= */}

          <div className="product-gallery">

            <div className="product-gallery-thumbs">

              {images.map(
                (
                  item,
                  index
                ) => (
                  <button
                    type="button"
                    key={`${item}-${index}`}
                    className={
                      activeImage ===
                      item
                        ? "gallery-thumb active"
                        : "gallery-thumb"
                    }
                    onClick={() =>
                      setActiveImage(
                        item
                      )
                    }
                    aria-label={`Ver imagen ${
                      index + 1
                    }`}
                  >
                    <img
                      src={item}
                      alt={`${product.name} ${
                        index + 1
                      }`}
                      onError={(
                        event
                      ) => {
                        event.currentTarget.src =
                          PLACEHOLDER_IMAGE;
                      }}
                    />
                  </button>
                )
              )}

            </div>


            <div className="product-main-image">

              <div className="product-image-badges">

                {product.is_featured && (
                  <span className="product-badge featured">
                    MÁS VENDIDO
                  </span>
                )}

                {discount > 0 && (
                  <span className="product-badge discount">
                    {discount}% OFF
                  </span>
                )}

              </div>


              {/* =================================================
                  FAVORITO
              ================================================= */}

              <button
                type="button"
                className={
                  favorite
                    ? "product-favorite active"
                    : "product-favorite"
                }
                onClick={
                  handleToggleFavorite
                }
                disabled={
                  favoriteLoading
                }
                aria-label={
                  favorite
                    ? "Quitar de favoritos"
                    : "Agregar a favoritos"
                }
                aria-pressed={
                  favorite
                }
              >
                <Heart
                  size={22}
                  fill={
                    favorite
                      ? "currentColor"
                      : "none"
                  }
                />
              </button>


              <img
                src={
                  activeImage ||
                  PLACEHOLDER_IMAGE
                }
                alt={
                  product.name
                }
                onError={(
                  event
                ) => {
                  event.currentTarget.src =
                    PLACEHOLDER_IMAGE;
                }}
              />

            </div>


            {images.length >
              1 && (
              <div className="product-gallery-controls">

                <button
                  type="button"
                  onClick={() => {
                    const currentIndex =
                      images.indexOf(
                        activeImage
                      );

                    const nextIndex =
                      currentIndex <=
                      0
                        ? images.length -
                          1
                        : currentIndex -
                          1;

                    setActiveImage(
                      images[
                        nextIndex
                      ]
                    );
                  }}
                  aria-label="Imagen anterior"
                >
                  <ArrowLeft
                    size={17}
                  />
                </button>

                <span>
                  {Math.max(
                    images.indexOf(
                      activeImage
                    ) + 1,
                    1
                  )}

                  {" / "}

                  {images.length}
                </span>

                <button
                  type="button"
                  onClick={() => {
                    const currentIndex =
                      images.indexOf(
                        activeImage
                      );

                    const nextIndex =
                      currentIndex >=
                      images.length - 1
                        ? 0
                        : currentIndex + 1;

                    setActiveImage(
                      images[
                        nextIndex
                      ]
                    );
                  }}
                  aria-label="Siguiente imagen"
                >
                  <ArrowRight
                    size={17}
                  />
                </button>

              </div>
            )}

          </div>


          {/* =================================================
              PRODUCT INFORMATION
          ================================================= */}

          <div className="product-information">

            <div className="product-category-line">

              <Tag
                size={14}
              />

              <span>
                {category}
              </span>

              {brand && (
                <>
                  <span className="category-separator">
                    /
                  </span>

                  <span>
                    {brand}
                  </span>
                </>
              )}

            </div>


            <div className="product-title-row">

              <div>

                <h1>
                  {product.name}
                </h1>

                {sku && (
                  <span className="product-reference">
                    REF. {sku}
                  </span>
                )}

              </div>

            </div>


            {/* =================================================
                RATING
            ================================================= */}

            <div className="product-rating-row">

              <div className="product-stars">

                {[0, 1, 2, 3, 4].map(
                  (item) => (
                    <Star
                      key={item}
                      size={19}
                      fill={
                        item <
                        Math.round(
                          rating
                        )
                          ? "currentColor"
                          : "none"
                      }
                    />
                  )
                )}

              </div>

              <strong>
                {rating.toFixed(
                  1
                )}
              </strong>

              <span>
                (
                {reviewCount ||
                  "124"}{" "}
                reseñas)
              </span>

            </div>


            {/* =================================================
                DESCRIPTION
            ================================================= */}

            <p className="product-description">
              {description}
            </p>


            {/* =================================================
                QUICK FEATURES
            ================================================= */}

            <div className="product-feature-strip">

              <div className="quick-feature">

                <Zap
                  size={22}
                />

                <div>

                  <strong>
                    EXPERIENCIA
                  </strong>

                  <span>
                    Premium
                  </span>

                </div>

              </div>


              <div className="quick-feature">

                <Truck
                  size={22}
                />

                <div>

                  <strong>
                    ENVÍO
                  </strong>

                  <span>
                    A todo Colombia
                  </span>

                </div>

              </div>


              <div className="quick-feature">

                <ShieldCheck
                  size={22}
                />

                <div>

                  <strong>
                    GARANTÍA
                  </strong>

                  <span>
                    Soporte incluido
                  </span>

                </div>

              </div>

            </div>


            {/* =================================================
                PRICE
            ================================================= */}

            <div className="product-price-area">

              <div className="product-price-main">

                <small>
                  PRECIO
                </small>

                <strong>
                  {currentPrice}
                </strong>

              </div>


              {originalPrice && (
                <div className="product-old-price">
                  {formatPrice(
                    originalPrice
                  )}
                </div>
              )}


              {discount > 0 && (
                <span className="product-price-discount">
                  {discount}% OFF
                </span>
              )}

            </div>


            {/* =================================================
                STOCK
            ================================================= */}

            <div className="product-stock-line">

              <span
                className={
                  outOfStock
                    ? "stock-indicator unavailable"
                    : "stock-indicator"
                }
              />

              <span>
                {outOfStock
                  ? "Producto sin stock"
                  : `Disponible · ${numericStock} unidades`}
              </span>

            </div>


            {/* =================================================
                PURCHASE
            ================================================= */}

            {!outOfStock && (
              <>

                <div className="product-purchase-row">

                  <div className="quantity-control">

                    <button
                      type="button"
                      onClick={
                        decreaseQuantity
                      }
                      disabled={
                        quantity <= 1
                      }
                      aria-label="Disminuir cantidad"
                    >
                      <Minus
                        size={16}
                      />
                    </button>


                    <input
                      type="number"
                      min="1"
                      max={
                        maxQuantity
                      }
                      value={
                        quantity
                      }
                      onChange={
                        handleQuantityInput
                      }
                      aria-label="Cantidad"
                    />


                    <button
                      type="button"
                      onClick={
                        increaseQuantity
                      }
                      disabled={
                        quantity >=
                        maxQuantity
                      }
                      aria-label="Aumentar cantidad"
                    >
                      <Plus
                        size={16}
                      />
                    </button>

                  </div>

                </div>


                {/* =================================================
                    CART MESSAGE
                ================================================= */}

                {cartMessage && (
                  <div className="product-cart-message">

                    <CheckCircle2
                      size={18}
                    />

                    <span>
                      {cartMessage}
                    </span>

                    <button
                      type="button"
                      onClick={
                        handleGoToCart
                      }
                    >
                      VER CARRITO

                      <ChevronRight
                        size={16}
                      />
                    </button>

                  </div>
                )}


                {/* =================================================
                    PURCHASE ACTIONS
                ================================================= */}

                <div className="product-purchase-actions">

                  <button
                    type="button"
                    className="product-add-cart-button"
                    onClick={
                      addProductToCart
                    }
                    disabled={
                      addingToCart ||
                      buyingNow
                    }
                  >

                    {addingToCart ? (
                      <>
                        <span className="product-button-spinner" />

                        AGREGANDO...
                      </>
                    ) : (
                      <>
                        <ShoppingCart
                          size={19}
                        />

                        AGREGAR AL CARRITO
                      </>
                    )}

                  </button>


                  <button
                    type="button"
                    className="product-buy-now-button"
                    onClick={
                      handleBuyNow
                    }
                    disabled={
                      buyingNow ||
                      addingToCart
                    }
                  >

                    {buyingNow ? (
                      <>
                        <span className="product-button-spinner" />

                        PREPARANDO...
                      </>
                    ) : (
                      <>
                        <Zap
                          size={19}
                        />

                        COMPRAR AHORA
                      </>
                    )}

                  </button>

                </div>

              </>
            )}


            {/* =================================================
                WHATSAPP
            ================================================= */}

            {!outOfStock && (
              <a
                href={
                  whatsappLink
                }
                target="_blank"
                rel="noreferrer"
                className="product-whatsapp-secondary"
              >

                <MessageCircle
                  size={19}
                />

                <span>

                  ¿Tienes dudas?

                  <strong>
                    Escríbenos por WhatsApp
                  </strong>

                </span>

                <ChevronRight
                  size={18}
                />

              </a>
            )}


            {/* =================================================
                BENEFITS
            ================================================= */}

            <div className="product-benefits">

              <div>

                <Truck
                  size={21}
                />

                <span>

                  <strong>
                    Envío rápido
                  </strong>

                  A todo Colombia

                </span>

              </div>


              <div>

                <ShieldCheck
                  size={21}
                />

                <span>

                  <strong>
                    Compra segura
                  </strong>

                  Atención personalizada

                </span>

              </div>


              <div>

                <BadgeCheck
                  size={21}
                />

                <span>

                  <strong>
                    Producto premium
                  </strong>

                  Selección Debuts Crop

                </span>

              </div>

            </div>

          </div>


          {/* =================================================
              RIGHT INFORMATION PANEL
          ================================================= */}

          <aside className="product-side-panel">

            <div className="side-promo">

              <span>
                DEBUTS CROP
              </span>

              <strong>
                DISTRIBUYE UNA
                <br />
                EXPERIENCIA PREMIUM
              </strong>

            </div>


            {/* =================================================
                FEATURES
            ================================================= */}

            <section className="side-section">

              <header>

                <h2>
                  Características principales
                </h2>

              </header>


              <div className="side-feature-list">

                {features.map(
                  (
                    feature,
                    index
                  ) => (
                    <div
                      className="side-feature"
                      key={`${feature}-${index}`}
                    >

                      <span className="feature-icon">

                        <Zap
                          size={15}
                        />

                      </span>


                      <span>
                        {feature}
                      </span>


                      <button
                        type="button"
                        onClick={() =>
                          setOpenFeature(
                            openFeature ===
                              index
                              ? null
                              : index
                          )
                        }
                        aria-label={`Ver característica ${
                          index + 1
                        }`}
                      >

                        <ChevronDown
                          size={16}
                          className={
                            openFeature ===
                            index
                              ? "rotated"
                              : ""
                          }
                        />

                      </button>


                      {openFeature ===
                        index && (
                        <div className="side-feature-detail">

                          {feature}

                        </div>
                      )}

                    </div>
                  )
                )}

              </div>

            </section>


            {/* =================================================
                BOX CONTENT
            ================================================= */}

            <section className="side-section">

              <header>

                <h2>
                  Contenido de la caja
                </h2>

                <Package
                  size={17}
                />

              </header>


              <div className="box-content-list">

                {safeBoxContents.map(
                  (
                    item,
                    index
                  ) => (
                    <div
                      key={`${item}-${index}`}
                    >

                      <Check
                        size={16}
                      />

                      <span>
                        {item}
                      </span>

                    </div>
                  )
                )}

              </div>

            </section>


            {/* =================================================
                PAYMENT
            ================================================= */}

            <section className="side-section payment-section">

              <header>

                <h2>
                  Métodos de pago
                </h2>

              </header>


              <div className="payment-methods">

                <span>
                  VISA
                </span>

                <span>
                  MC
                </span>

                <span>
                  Nequi
                </span>

                <span>
                  PSE
                </span>

              </div>

            </section>

          </aside>

        </section>


        {/* =================================================
            PRODUCT TABS
        ================================================= */}

        <section className="product-content-area">

          <div className="product-tabs">

            <button
              type="button"
              className={
                activeTab ===
                "description"
                  ? "active"
                  : ""
              }
              onClick={() =>
                setActiveTab(
                  "description"
                )
              }
            >
              Descripción
            </button>


            <button
              type="button"
              className={
                activeTab ===
                "specifications"
                  ? "active"
                  : ""
              }
              onClick={() =>
                setActiveTab(
                  "specifications"
                )
              }
            >
              Especificaciones
            </button>


            <button
              type="button"
              className={
                activeTab ===
                "reviews"
                  ? "active"
                  : ""
              }
              onClick={() =>
                setActiveTab(
                  "reviews"
                )
              }
            >
              Opiniones (
              {reviewCount ||
                124}
              )
            </button>


            <button
              type="button"
              className={
                activeTab ===
                "faq"
                  ? "active"
                  : ""
              }
              onClick={() =>
                setActiveTab(
                  "faq"
                )
              }
            >
              Preguntas frecuentes
            </button>

          </div>


          <div className="product-tab-content">

            {/* =================================================
                DESCRIPTION
            ================================================= */}

            {activeTab ===
              "description" && (
              <div className="description-layout">

                <div className="description-copy">

                  <span className="technical-label">
                    DEBUTS CROP · PRODUCT EXPERIENCE
                  </span>

                  <h2>
                    CALIDAD PREMIUM.
                    <br />
                    EXPERIENCIA EN CADA DETALLE.
                  </h2>

                  <p>
                    {description}
                  </p>

                  <p>
                    En Debuts Crop seleccionamos
                    productos pensando en calidad,
                    diseño, funcionalidad y experiencia.
                    Puedes comprar directamente desde
                    esta página y continuar al checkout.
                  </p>

                </div>


                <div className="description-image">

                  <img
                    src={
                      images[0] ||
                      PLACEHOLDER_IMAGE
                    }
                    alt={
                      product.name
                    }
                    onError={(
                      event
                    ) => {
                      event.currentTarget.src =
                        PLACEHOLDER_IMAGE;
                    }}
                  />

                  <span>
                    PURO SABOR.
                    <br />
                    MÁXIMO CONTROL.
                  </span>

                </div>

              </div>
            )}


            {/* =================================================
                SPECIFICATIONS
            ================================================= */}

            {activeTab ===
              "specifications" && (
              <div className="specifications-panel">

                {safeSpecifications.map(
                  (
                    specification,
                    index
                  ) => (
                    <div
                      className="specification-row"
                      key={`${specification}-${index}`}
                    >

                      <span>
                        {specification}
                      </span>

                      <Check
                        size={17}
                      />

                    </div>
                  )
                )}

              </div>
            )}


            {/* =================================================
                REVIEWS
            ================================================= */}

            {activeTab ===
              "reviews" && (
              <div className="reviews-panel">

                <div className="review-summary">

                  <strong>
                    {rating.toFixed(
                      1
                    )}
                  </strong>

                  <div>

                    <div className="product-stars">

                      {[0, 1, 2, 3, 4].map(
                        (item) => (
                          <Star
                            key={item}
                            size={20}
                            fill="currentColor"
                          />
                        )
                      )}

                    </div>

                    <span>
                      Basado en{" "}
                      {reviewCount ||
                        124}{" "}
                      reseñas
                    </span>

                  </div>

                </div>


                <p>
                  Las opiniones disponibles
                  del producto se muestran
                  aquí cuando son entregadas
                  por el API.
                </p>

              </div>
            )}


            {/* =================================================
                FAQ
            ================================================= */}

            {activeTab ===
              "faq" && (
              <div className="faq-panel">

                <div className="faq-item">

                  <strong>
                    ¿Cómo puedo comprar este producto?
                  </strong>

                  <p>
                    Selecciona la cantidad y
                    pulsa “Comprar ahora”.
                    El producto se agregará
                    al carrito y podrás
                    continuar con el checkout.
                  </p>

                </div>


                <div className="faq-item">

                  <strong>
                    ¿Puedo agregar varios productos?
                  </strong>

                  <p>
                    Sí. Puedes agregar productos
                    al carrito, continuar
                    comprando y posteriormente
                    realizar un único checkout.
                  </p>

                </div>


                <div className="faq-item">

                  <strong>
                    ¿Cómo funciona el pago?
                  </strong>

                  <p>
                    En el checkout podrás
                    seleccionar la modalidad
                    de entrega, revisar el
                    total y realizar el proceso
                    de pago configurado por
                    Debuts Crop.
                  </p>

                </div>


                <div className="faq-item">

                  <strong>
                    ¿El producto está disponible?
                  </strong>

                  <p>
                    El estado de stock mostrado
                    en esta página proviene del
                    producto entregado por el API.
                  </p>

                </div>

              </div>
            )}

          </div>

        </section>


        {/* =================================================
            RELATED PRODUCTS
        ================================================= */}

        {relatedProducts.length >
          0 && (
          <section className="related-products-section">

            <div className="section-heading">

              <div>

                <span className="technical-label">
                  TAMBIÉN TE PUEDE INTERESAR
                </span>

                <h2>
                  Productos relacionados
                </h2>

              </div>


              <div className="related-arrows">

                <button
                  type="button"
                  aria-label="Anterior"
                >
                  <ArrowLeft
                    size={17}
                  />
                </button>

                <button
                  type="button"
                  aria-label="Siguiente"
                >
                  <ArrowRight
                    size={17}
                  />
                </button>

              </div>

            </div>


            <div className="related-products-grid">

              {relatedProducts.map(
                (
                  related,
                  index
                ) => {

                  const relatedImage =
                    getImage(
                      related?.image ||
                        related?.image_url ||
                        related?.thumbnail
                    ) ||
                    PLACEHOLDER_IMAGE;

                  const relatedPrice =
                    related?.formatted_price ||
                    formatPrice(
                      related?.price
                    );

                  return (
                    <article
                      className="related-product-card"
                      key={
                        getProductId(
                          related
                        ) ||
                        `${related?.name}-${index}`
                      }
                      onDoubleClick={() =>
                        handleRelatedProduct(
                          related
                        )
                      }
                    >

                      <div className="related-image">

                        <img
                          src={
                            relatedImage
                          }
                          alt={
                            related?.name ||
                            "Producto relacionado"
                          }
                          onError={(
                            event
                          ) => {
                            event.currentTarget.src =
                              PLACEHOLDER_IMAGE;
                          }}
                        />


                        <button
                          type="button"
                          aria-label="Ver producto"
                          onClick={() =>
                            handleRelatedProduct(
                              related
                            )
                          }
                        >
                          <ChevronRight
                            size={18}
                          />
                        </button>

                      </div>


                      <div className="related-info">

                        <span>
                          {related?.category_name ||
                            related?.category ||
                            "DEBUTS CROP"}
                        </span>

                        <h3>
                          {related?.name ||
                            "Producto"}
                        </h3>

                        <strong>
                          {relatedPrice}
                        </strong>

                      </div>

                    </article>
                  );
                }
              )}

            </div>

          </section>
        )}


        {/* =================================================
            MOBILE PURCHASE BAR
        ================================================= */}

        {!outOfStock && (
          <div className="mobile-whatsapp-bar">

            <div>

              <span>
                {currentPrice}
              </span>

              <small>
                Compra directa
              </small>

            </div>


            <button
              type="button"
              onClick={
                handleBuyNow
              }
              disabled={
                buyingNow ||
                addingToCart
              }
            >

              {buyingNow ? (
                <>
                  <span className="product-button-spinner" />

                  PREPARANDO...
                </>
              ) : (
                <>
                  <ShoppingCart
                    size={19}
                  />

                  COMPRAR
                </>
              )}

            </button>

          </div>
        )}

      </main>

      <Footer />
    </>
  );
}